import { ToolStatus } from "@optidev/shared-types";

import type { ActiveTool, TextToolSegment } from "./AgentMessage";

/**
 * Phase grouping — collapse a long assistant turn (dozens of interleaved text
 * chunks + tool cards) into a handful of named "phases" the user can scan.
 *
 * ENGINE-AGNOSTIC BY DESIGN. Both agent engines (Claude Agent SDK and OpenCode,
 * chosen by model in the gateway) converge on the same normalized tool stream
 * before this code runs. So we MUST key phase detection off normalized signals
 * only — and we categorize tools by case-insensitive substring of the tool
 * name (never exact equality like `=== "Bash"`), because tool names differ
 * across engines (Claude `Bash`/`Read`/`Edit` vs OpenCode lowercase/variants).
 * This mirrors the existing pattern in claude-session-manager.service.ts.
 */

export type PhaseKind =
  | "research"
  | "plan"
  | "setup"
  | "build"
  | "theme"
  | "review"
  | "work"
  | "closing";

export type PhaseStatus = "running" | "done" | "error";

export interface Phase {
  /** Stable-ish key for React (kind + start index). */
  id: string;
  kind: PhaseKind;
  /** Collapsed/finished label, e.g. "Built it". */
  label: string;
  /** In-flight label, e.g. "Building". */
  runningLabel: string;
  status: PhaseStatus;
  /** Global index (into the full segments array) of this phase's first segment. */
  startIndex: number;
  segments: TextToolSegment[];
  /** Total tools across this phase's segments. */
  toolCount: number;
  /** Short outcome line shown under the label, e.g. "12 files". */
  outcome?: string;
}

interface PhaseMeta {
  label: string;
  runningLabel: string;
}

const PHASE_META: Record<PhaseKind, PhaseMeta> = {
  research: { label: "Researched your site", runningLabel: "Researching your site" },
  plan: { label: "Planned it", runningLabel: "Planning" },
  setup: { label: "Set up the project", runningLabel: "Setting up" },
  build: { label: "Built it", runningLabel: "Building" },
  theme: { label: "Applied the theme", runningLabel: "Applying the theme" },
  review: { label: "Reviewed the result", runningLabel: "Reviewing" },
  work: { label: "Worked on it", runningLabel: "Working" },
  // Default label for the agent's closing message. When the turn instead hands
  // control back to the user (a trailing question / HITL card), the label
  // becomes CLOSING_HANDBACK_LABEL — see splitTrailingSummary.
  closing: { label: "Wrap-up", runningLabel: "Wrapping up" },
};

/** Closing-node label when the turn ends by putting a card in front of the user. */
const CLOSING_HANDBACK_LABEL = "Over to you";

/**
 * Map a single tool to a "decisive" phase kind, or null if the tool is
 * ambiguous (read/grep/bash/ls/todo/viewport) — those inherit the surrounding
 * phase so a lone Read inside a build run doesn't split it. Substring matching
 * keeps this engine-agnostic.
 */
function decisiveKind(toolName: string): PhaseKind | null {
  const n = toolName.toLowerCase();

  // Highest-signal tools first.
  if (n.includes("plan")) return "plan";
  if (n.includes("theme")) return "theme";
  // Preview *checks* are review; `set_preview_viewport` just sizes the canvas
  // (it carries "preview" too) — exclude it so it inherits the surrounding phase.
  if ((n.includes("preview") || n.includes("screenshot")) && !n.includes("viewport")) {
    return "review";
  }
  // Web research (WebFetch / WebSearch / fetch_webpage / crawl).
  if (n.includes("web") || n.includes("fetch") || n.includes("crawl")) {
    return "research";
  }
  // Authoring code.
  if (n.includes("write") || n.includes("edit")) return "build";

  return null;
}

/** The kind a segment "declares" via its first decisive tool, else null. */
function segmentKind(segment: TextToolSegment): PhaseKind | null {
  for (const tool of segment.tools) {
    const k = decisiveKind(tool.toolName);
    if (k) return k;
  }
  return null;
}

function extractFilePath(tool: ActiveTool): string | null {
  const input = tool.toolInput;
  if (!input) return null;
  const p = input.file_path ?? input.path ?? input.filePath;
  return typeof p === "string" ? p : null;
}

/** Derive a short outcome line from the tools a phase ran. */
function deriveOutcome(kind: PhaseKind, tools: ActiveTool[]): string | undefined {
  if (kind === "build") {
    const files = new Set<string>();
    for (const t of tools) {
      const n = t.toolName.toLowerCase();
      if (n.includes("write") || n.includes("edit")) {
        const fp = extractFilePath(t);
        if (fp) files.add(fp);
      }
    }
    if (files.size > 0) return `${files.size} file${files.size > 1 ? "s" : ""}`;
  }
  if (kind === "research") {
    const sources = tools.filter((t) => {
      const n = t.toolName.toLowerCase();
      return n.includes("web") || n.includes("fetch") || n.includes("crawl");
    }).length;
    if (sources > 0) return `Explored ${sources} source${sources > 1 ? "s" : ""}`;
  }
  return undefined;
}

function phaseStatus(tools: ActiveTool[]): PhaseStatus {
  if (tools.some((t) => t.status === ToolStatus.RUNNING)) return "running";
  return "done";
}

// ============================================================
// Primary path: agent-authored phases from TodoWrite
// ============================================================
//
// Both engines emit a todo tool — Claude Code `TodoWrite`, OpenCode
// `todowrite` — with the SAME shape: `tool_input.todos` is an array of
// `{ status, content, activeForm? }`, status ∈ pending|in_progress|completed.
// OpenCode omits `activeForm` (and adds `priority`), so the label falls back to
// `content`. Tool-name match is case-insensitive substring, so both engines
// (and any future variant) are covered. The agent's own todo wording becomes
// the phase labels — dynamic, content-specific, and scalable (no hardcoded
// label table, no per-task tuning).

interface RawTodo {
  status?: unknown;
  content?: unknown;
  activeForm?: unknown;
}

function asString(v: unknown): string {
  return typeof v === "string" ? v : "";
}

/** Is this a todo tool? Returns its todo array, or null if it isn't one. */
function readTodos(tool: ActiveTool): RawTodo[] | null {
  if (!tool.toolName.toLowerCase().includes("todo")) return null;
  const todos = tool.toolInput?.todos;
  return Array.isArray(todos) ? (todos as RawTodo[]) : null;
}

/** Label of the first in-progress todo in a list, or null if none is active. */
function activeTodoLabel(todos: RawTodo[]): string | null {
  const active = todos.find((t) => asString(t.status) === "in_progress");
  if (!active) return null;
  const label = (asString(active.activeForm) || asString(active.content)).trim();
  return label || null;
}

function deriveFileOutcome(tools: ActiveTool[]): string | undefined {
  const files = new Set<string>();
  for (const t of tools) {
    const n = t.toolName.toLowerCase();
    if (n.includes("write") || n.includes("edit")) {
      const fp = extractFilePath(t);
      if (fp) files.add(fp);
    }
  }
  return files.size > 0 ? `${files.size} file${files.size > 1 ? "s" : ""}` : undefined;
}

/**
 * Group segments into phases using the agent's own TodoWrite list.
 *
 * CRUCIAL: the agent often fires several TodoWrite updates with NO narration
 * text between them, so multiple todo transitions can live inside ONE text
 * segment. We therefore split WITHIN a segment — walking its tools in order and
 * starting a new phase at every change of the in-progress todo. (Grouping by
 * whole segments instead would collapse those transitions into one node whose
 * title just mutates to the latest todo, losing every intermediate phase.)
 *
 * Append-only: a later todo never relabels an earlier phase, so the timeline is
 * stable while streaming. Work before the first todo becomes "Getting started".
 * The todo tool itself is bookkeeping and is never counted as a step. Returns
 * null when the turn has no usable todos (→ heuristic fallback).
 */
function groupByTodos(segments: TextToolSegment[]): Phase[] | null {
  const hasTodos = segments.some((s) => s.tools.some((t) => readTodos(t)));
  if (!hasTodos) return null;

  const LEADING = "Getting started";
  const phases: Phase[] = [];
  let current: Phase | null = null;
  let label = LEADING;

  // Append `text` + `tools` to the phase for `label`, opening a new phase when
  // the label changes (so consecutive same-label runs stay one phase).
  const push = (text: string, tools: ActiveTool[]): void => {
    if (!text && tools.length === 0) return;
    if (!current || current.label !== label) {
      current = {
        id: `todo-${phases.length}`,
        kind: "work",
        label,
        runningLabel: label,
        status: "done",
        startIndex: 0, // reindexed across the flattened segments below
        segments: [],
        toolCount: 0,
        outcome: undefined,
      };
      phases.push(current);
    }
    current.segments.push({ text, tools });
  };

  for (const segment of segments) {
    let pendingText = segment.text;
    let runTools: ActiveTool[] = [];
    const flush = (): void => {
      push(pendingText, runTools);
      pendingText = "";
      runTools = [];
    };

    for (const tool of segment.tools) {
      const todos = readTodos(tool);
      if (todos) {
        const next = activeTodoLabel(todos);
        if (next && next !== label) {
          flush(); // close the run that belongs to the previous todo
          label = next;
        }
        continue; // the todo tool is bookkeeping, not a visible step
      }
      runTools.push(tool);
    }
    flush();
  }

  // Reindex startIndex across the flattened synthetic segments; finalize counts.
  let acc = 0;
  for (const phase of phases) {
    phase.startIndex = acc;
    acc += phase.segments.length;
    const tools = phase.segments.flatMap((s) => s.tools);
    phase.toolCount = tools.length; // todo tools already excluded
    phase.outcome = deriveFileOutcome(tools);
  }

  return phases.length >= 1 ? phases : null;
}

/**
 * Peel the agent's closing message into its own phase ("Wrap-up", or
 * "Over to you" when the turn hands control back to the user).
 *
 * The agent's todos give us the work phases, but the prose it writes after the
 * last todo completes is NOT a todo — it lands inside the final work phase. So
 * we detect it ourselves, using only signals that are unambiguous on a COMPLETED
 * turn (the caller never runs this while streaming):
 *   - it's the contiguous run of text-only segments AFTER the last tool-bearing
 *     segment in the final phase (earlier commentary stays with its work phase);
 *   - real tool work preceded it (a pure chat reply isn't a closing message);
 *   - it's substantial (≥ MIN chars) — a "Done! 🎉" one-liner stays put.
 *
 * `awaitsUserInput` (the turn ends with a question / HITL card the user must act
 * on) flips the label to "Over to you". Degrades gracefully: a miss leaves the
 * prose in the last phase (status quo); it never alters the agent's own labels.
 */
const CLOSING_MIN_CHARS = 120;

function splitTrailingSummary(
  phases: Phase[],
  awaitsUserInput: boolean,
): Phase[] {
  if (phases.length === 0) return phases;
  const last = phases[phases.length - 1];
  if (last.kind === "closing") return phases; // idempotent

  const label = awaitsUserInput
    ? CLOSING_HANDBACK_LABEL
    : PHASE_META.closing.label;

  const segs = last.segments;
  let lastToolIdx = -1;
  for (let i = 0; i < segs.length; i++) {
    if (segs[i].tools.length > 0) lastToolIdx = i;
  }

  const trailing = segs.slice(lastToolIdx + 1);
  const closing = trailing.map((s) => s.text).join("").trim();
  if (closing.length < CLOSING_MIN_CHARS) return phases;

  const keep = segs.slice(0, lastToolIdx + 1);

  // The closing prose trails real work in this phase → peel it into its own node.
  if (keep.length > 0) {
    last.segments = keep;
    last.toolCount = keep.reduce((n, s) => n + s.tools.length, 0);
    const startIndex = last.startIndex + keep.length;
    return [
      ...phases,
      {
        id: `closing-${startIndex}`,
        kind: "closing",
        label,
        runningLabel: PHASE_META.closing.runningLabel,
        status: "done",
        startIndex,
        segments: trailing,
        toolCount: 0,
        outcome: undefined,
      },
    ];
  }

  // The whole final phase is the closing message (no tools) and earlier work
  // exists → relabel it rather than spawn an empty work node beside it.
  if (phases.length >= 2) {
    last.kind = "closing";
    last.label = label;
    last.runningLabel = PHASE_META.closing.runningLabel;
  }
  return phases;
}

/** Where a turn's phases came from — drives the streaming-vs-completed gate. */
export interface PhaseResult {
  phases: Phase[];
  /** "todo" = agent-authored (append-only, safe to stream live). "heuristic" =
   *  tool-bucket guess (look-ahead seed isn't stream-stable → completed only). */
  source: "todo" | "heuristic" | "none";
}

/**
 * Public entry point. Prefer the agent's own phases (TodoWrite); fall back to
 * the tool-heuristic only when a turn has no todos. Reports the source so the
 * caller can stream the (stable) todo timeline live but hold the heuristic one
 * to completed turns.
 */
export function groupSegmentsIntoPhases(
  segments: TextToolSegment[],
  opts?: { canSplitClosing?: boolean; awaitsUserInput?: boolean },
): PhaseResult {
  if (!segments || segments.length === 0) return { phases: [], source: "none" };
  const todo = groupByTodos(segments);
  const phases = todo ?? groupByHeuristic(segments);
  const source: PhaseResult["source"] = todo ? "todo" : "heuristic";
  // Peel the closing message ONLY when the caller says the turn ended naturally
  // (`canSplitClosing`): not while streaming (more tools may follow), and not
  // when stopped/errored (no genuine close → would mislabel the last phase).
  const finalPhases = opts?.canSplitClosing
    ? splitTrailingSummary(phases, opts?.awaitsUserInput ?? false)
    : phases;
  return { phases: finalPhases, source };
}

/**
 * Group an assistant turn's interleaved segments into ordered phases.
 *
 * Algorithm (forward-only, deterministic):
 *  - Each segment declares a kind via its first decisive tool, or inherits the
 *    previous kind when it has none (text-only or read/bash/todo-only).
 *  - Consecutive segments of the same kind merge into one phase.
 *  - Seeded with a CONSTANT "setup" kind (NOT a look-ahead). This is what makes
 *    the heuristic STREAM-STABLE / append-only: a tool arriving later can only
 *    open a NEW phase, never retroactively relabel an earlier one. (A look-ahead
 *    seed would flip the first phase's label as new decisive tools stream in —
 *    flicker.) It also gives a normal build a real shape: Setting up → Building
 *    → Reviewing, instead of one flat blob.
 *
 * This is the live-safe baseline used whenever a turn has no readable TodoWrite
 * (which includes the entire streaming window, since live tool inputs arrive
 * without the todo payload). When todos resolve, `groupByTodos` takes over with
 * the agent's own labels.
 */
function groupByHeuristic(segments: TextToolSegment[]): Phase[] {
  if (!segments || segments.length === 0) return [];

  const phases: Phase[] = [];
  let current: Phase | null = null;
  // Constant seed → append-only → safe to render live (see doc above).
  let prevKind: PhaseKind = "setup";

  segments.forEach((segment, idx) => {
    const kind = segmentKind(segment) ?? prevKind;

    if (!current || kind !== current.kind) {
      const meta = PHASE_META[kind];
      current = {
        id: `${kind}-${idx}`,
        kind,
        label: meta.label,
        runningLabel: meta.runningLabel,
        status: "done",
        startIndex: idx,
        segments: [],
        toolCount: 0,
        outcome: undefined,
      };
      phases.push(current);
    }

    current.segments.push(segment);
    current.toolCount += segment.tools.length;
    prevKind = kind;
  });

  const coalesced = coalescePhases(phases);

  // Final pass: status + outcome (need the whole phase's tools).
  for (const phase of coalesced) {
    const tools = phase.segments.flatMap((s) => s.tools);
    phase.status = phaseStatus(tools);
    phase.outcome = deriveOutcome(phase.kind, tools);
  }

  return coalesced;
}

/** Fold one phase's segments into another (keeps `into`'s identity/kind/label). */
function absorb(into: Phase, from: Phase): void {
  into.segments.push(...from.segments);
  into.toolCount += from.toolCount;
}

/**
 * Smooth out fragmentation so phases stay scannable:
 *  - empty (0-tool, text-only) phases fold into the previous phase;
 *  - a tiny (≤1-tool) phase sandwiched between two larger phases folds into the
 *    previous one (e.g. a lone "re-add the font" edit between theme + final
 *    check shouldn't be its own row) — but a tiny *trailing* phase (like a final
 *    one-shot review) is kept;
 *  - adjacent same-kind phases merge.
 * A trailing tiny phase is preserved so closing rows like "Reviewed" survive.
 */
function coalescePhases(phases: Phase[]): Phase[] {
  if (phases.length <= 1) return phases;

  const out: Phase[] = [];
  phases.forEach((phase, i) => {
    const prev = out[out.length - 1];
    const isLast = i === phases.length - 1;
    const tiny = phase.toolCount <= 1;

    if (prev) {
      // Same kind → always merge.
      if (prev.kind === phase.kind) {
        absorb(prev, phase);
        return;
      }
      // Empty, or a tiny non-trailing aside → fold into the previous phase.
      if (phase.toolCount === 0 || (tiny && !isLast)) {
        absorb(prev, phase);
        return;
      }
    }
    out.push(phase);
  });

  return out;
}

/**
 * Phase-grouping rollout switch. Default ON; override per-browser via
 * `localStorage.setItem('optidev:phaseGrouping', 'off' | 'on')` while we iterate.
 */
export function phaseGroupingEnabled(): boolean {
  try {
    const v = localStorage.getItem("optidev:phaseGrouping");
    if (v === "off") return false;
    if (v === "on") return true;
  } catch {
    /* SSR / privacy mode — fall through to default */
  }
  return true;
}
